/**
 * Error Handling Utilities
 * 
 * This file contains utilities for standardized error handling across the application.
 * It implements the error handling strategy defined in docs/error-handling-strategy.md.
 */

import {ApiError} from '@/services/api/types';
import {useToast} from "@fs/uikit-react";

/**
 * Categories of errors that can occur in the application
 */
export enum ErrorCategory {
  VALIDATION = 'VALIDATION',
  AUTHENTICATION = 'AUTHENTICATION',
  AUTHORIZATION = 'AUTHORIZATION',
  NOT_FOUND = 'NOT_FOUND',
  SERVER = 'SERVER',
  NETWORK = 'NETWORK',
  TIMEOUT = 'TIMEOUT',
  CANCELLED = 'CANCELLED',
  UNKNOWN = 'UNKNOWN',
}

/**
 * Categorizes an API error based on its status code and other properties
 * @param error - The API error to categorize
 * @returns The error category
 */
export function categorizeError(error: ApiError): ErrorCategory {
  // Check for specific error codes first
  if (error.code === 'NETWORK_ERROR' || error.code === 'CLIENT_NETWORK_ERROR') {
    return ErrorCategory.NETWORK;
  }

  if (error.code === 'CANCELLED' || error.code === 'CLIENT_REQUEST_CANCELLED') {
    return ErrorCategory.CANCELLED;
  }

  if (error.code === 'TIMEOUT' || error.code === 'CLIENT_TIMEOUT') {
    return ErrorCategory.TIMEOUT;
  }

  // Then check HTTP status codes
  if (!error.status) {
    return ErrorCategory.UNKNOWN;
  }

  if (error.status === 401) {
    return ErrorCategory.AUTHENTICATION;
  }

  if (error.status === 403) {
    return ErrorCategory.AUTHORIZATION;
  }

  if (error.status === 404) {
    return ErrorCategory.NOT_FOUND;
  }

  if (error.status === 400 || error.status === 422) {
    return ErrorCategory.VALIDATION;
  }

  if (error.status >= 500) {
    return ErrorCategory.SERVER;
  }

  return ErrorCategory.UNKNOWN;
}

/**
 * Gets a user-friendly error message based on the error category
 * @param error - The API error
 * @returns A user-friendly error message
 */
export function getUserFriendlyErrorMessage(error: ApiError): string {
  const category = categorizeError(error);
  let message = '';

  switch (category) {
    case ErrorCategory.VALIDATION:
      message = error.message || 'Перевірте правильність введених даних.';
      break;
    case ErrorCategory.AUTHENTICATION:
      message = 'Необхідно увійти в систему для виконання цієї дії.';
      break;
    case ErrorCategory.AUTHORIZATION:
      message = 'У вас немає прав для виконання цієї дії.';
      break;
    case ErrorCategory.NOT_FOUND:
      message = 'Запитаний ресурс не знайдено.';
      break;
    case ErrorCategory.SERVER:
      message = 'Виникла помилка на сервері. Спробуйте пізніше.';
      break;
    case ErrorCategory.NETWORK:
      message = 'Помилка мережі. Перевірте підключення до інтернету.';
      break;
    case ErrorCategory.TIMEOUT:
      message = 'Час очікування відповіді від сервера минув. Спробуйте пізніше.';
      break;
    case ErrorCategory.CANCELLED:
      message = 'Запит було скасовано.';
      break;
    case ErrorCategory.UNKNOWN:
    default:
      message = error.message || 'Сталася невідома помилка. Спробуйте ще раз.';
      break;
  }

  // Add detailed error information if available
  if (error.details) {
    // For validation errors, include field errors
    if (error.details.fieldErrors) {
      const fieldErrors = Object.values(error.details.fieldErrors).join(', ');
      message = `${message}: ${fieldErrors}`;
    } 
    // For resource not found errors, include resource details
    else if (error.details.resourceName && error.details.fieldValue) {
      message = `${message}: ${error.details.resourceName} з ${error.details.fieldName || 'id'} "${error.details.fieldValue}" не знайдено`;
    }
    // For other errors, include any additional details
    else if (error.details.error) {
      message = `${message}: ${error.details.error}`;
    }
  }

  return message;
}

/**
 * Hook for handling API errors in a standardized way
 * @returns Functions for handling API errors
 */
export function useApiErrorHandler() {
  const toast = useToast();

  /**
   * Handles an API error by showing an appropriate toast notification
   * @param error - The API error to handle
   * @param title - Optional custom title for the error toast
   */
  const handleApiError = (error: ApiError, title?: string) => {
    const category = categorizeError(error);
    const message = getUserFriendlyErrorMessage(error);

    // Log detailed error information for debugging
    console.error(`API Error [${category}]:`, error);

    // Show toast notification with user-friendly message
    toast.showErrorToast(
      title || 'Помилка',
      message
    );
  };

  return {
    handleApiError,
    categorizeError,
  };
}
